import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { ApiLoadingState } from '../../../../../middleware/api/apiList'
import mileStoneApi from '../../../../../middleware/api/ProjectManagement/Projects/ProjectView/MileStone/mileStoneApi'
import { AppDispatch, RootState } from '../../../../../stateStore'
import { LoadingState, ValidationError } from '../../../../../types/commonTypes'
import { ChangeRequestProps } from '../../../../../types/ProjectManagement/Project/ProjectView/ChangeRequest/changeRequestTypes'
import {
  AddMilestoneProps,
  GetCRListForMilestone,
  GetMilestone,
  GetPeopleForMilestone,
  GetWorkDetails,
  GetWorkDetailsProps,
  MileStoneDiscussionProps,
  MileStoneHistory,
  MilestoneNewsFeed,
  MileStoneResponse,
  MileStoneSliceState,
  PostMileStoneProps,
  RaisedInvoiceType,
  updateMilestoneProps,
} from '../../../../../types/ProjectManagement/Project/ProjectView/MileStone/mileStoneTypes'

const getProjectMileStone = createAsyncThunk(
  'projectView/getProjectMileStone',
  async (props: ChangeRequestProps, thunkApi) => {
    try {
      return await mileStoneApi.getProjectMileStone(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const mileStoneTimeLine = createAsyncThunk<
  MileStoneHistory[],
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('projectView/mileStoneTimeLine', async (id: number, thunkApi) => {
  try {
    return await mileStoneApi.mileStoneTimeLine(id)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const getMilestone = createAsyncThunk<
  GetMilestone,
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('projectView/getMilestone', async (milestoneId: number, thunkApi) => {
  try {
    return await mileStoneApi.getMilestone(milestoneId)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const uploadProjectMileStoneImage = createAsyncThunk(
  'projectView/uploadProjectMileStoneImage',
  async (prepareObject: { postid: number; file: FormData }, thunkApi) => {
    try {
      return await mileStoneApi.uploadProjectMileStoneImage(prepareObject)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const postProjectMileStone = createAsyncThunk<
  number | string,
  PostMileStoneProps,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'projectNotes/postProjectNotes',
  async (postMileStoneProps: PostMileStoneProps, thunkApi) => {
    try {
      return await mileStoneApi.postProjectMileStone(postMileStoneProps)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getMilestoneNewsFeed = createAsyncThunk(
  'projectView/getMilestoneNewsFeed',
  async (props: MileStoneDiscussionProps, thunkApi) => {
    try {
      return await mileStoneApi.getMilestoneNewsFeed(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const milestoneDelete = createAsyncThunk<
  number | undefined,
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('projectView/milestoneDelete', async (milestoneId, thunkApi) => {
  try {
    return await mileStoneApi.milestoneDelete(milestoneId)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const closeMilestone = createAsyncThunk<
  number | undefined,
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('projectView/closeMilestone', async (milestoneId, thunkApi) => {
  try {
    return await mileStoneApi.closeMilestone(milestoneId)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const reOpenMilestone = createAsyncThunk<
  number | undefined,
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('projectView/reOpenMilestone', async (milestoneId, thunkApi) => {
  try {
    return await mileStoneApi.reOpenMilestone(milestoneId)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const milestonePercentage = createAsyncThunk<
  number | undefined,
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('projectView/milestonePercentage', async (milestoneId, thunkApi) => {
  try {
    return await mileStoneApi.milestonePercentage(milestoneId)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const editProjectMilestone = createAsyncThunk<
  GetMilestone | undefined,
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('projectView/editProjectMilestone', async (milestoneId: number, thunkApi) => {
  try {
    return await mileStoneApi.editProjectMilestone(milestoneId)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const getPeopleForMilestone = createAsyncThunk<
  GetPeopleForMilestone[] | undefined,
  number | string,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'projectNotes/GetPeopleForMilestone',
  async (projectId: number | string, thunkApi) => {
    try {
      return await mileStoneApi.getPeopleForMilestone(projectId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getMilestoneNumber = createAsyncThunk<
  number | undefined,
  number | string,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'projectNotes/getMilestoneNumber',
  async (projectId: number | string, thunkApi) => {
    try {
      return await mileStoneApi.getMilestoneNumber(projectId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getCRListForMilestone = createAsyncThunk<
  GetCRListForMilestone[] | undefined,
  number | string,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'projectNotes/GetCRListForMilestone',
  async (projectid: number | string, thunkApi) => {
    try {
      return await mileStoneApi.getCRListForMilestone(projectid)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getWorkDetails = createAsyncThunk(
  'projectView/getWorkDetails',
  async (props: GetWorkDetailsProps, thunkApi) => {
    try {
      return await mileStoneApi.getWorkDetails(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const addProjectMilestone = createAsyncThunk<
  number | undefined,
  AddMilestoneProps,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'projectView/addProjectMilestone',
  async (milestone: AddMilestoneProps, thunkApi) => {
    try {
      return await mileStoneApi.addProjectMilestone(milestone)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const updateProjectMilestone = createAsyncThunk<
  number | undefined,
  updateMilestoneProps,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'projectView/updateProjectMilestone',
  async (milestone: updateMilestoneProps, thunkApi) => {
    try {
      return await mileStoneApi.updateProjectMilestone(milestone)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getRaisedInvoices = createAsyncThunk<
  RaisedInvoiceType[],
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('projectView/getRaisedInvoices', async (milestoneId, thunkApi) => {
  try {
    return await mileStoneApi.getRaisedInvoices(milestoneId)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const initialMileStoneState: MileStoneSliceState = {
  mileStonesList: { size: 0, list: [] },
  isLoading: ApiLoadingState.idle,
  currentPage: 1,
  pageSize: 20,
  mileStoneHistory: [],
  getMilestone: {} as GetMilestone,
  milestoneNewsFeed: [],
  getPeopleForMilestone: [] as GetPeopleForMilestone[],
  milestoneNumber: 0,
  getCRListForMilestone: [],
  getWorkDetails: {} as GetWorkDetails,
  milestonePercentageNumber: '',
  projectDetaildID: 0,
  objectForAdd: {
    firstIndex: 0,
    endIndex: 20,
    page: 0,
    size: 0,
    projectId: '',
  },
  isPagAdd: false,
  raisedInvoices: [] as RaisedInvoiceType[],
}

const mileStoneSlice = createSlice({
  name: 'projectView',
  initialState: initialMileStoneState,
  reducers: {
    setProjectDetailID: (state, action) => {
      state.projectDetaildID = action.payload
    },
    setObjectForAdd: (state, action) => {
      state.objectForAdd = action.payload
    },
    setIsPagAdd: (state, action) => {
      state.isPagAdd = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addProjectMilestone.fulfilled, (state) => {
      state.isLoading = ApiLoadingState.succeeded
    })
    builder.addCase(addProjectMilestone.pending, (state) => {
      state.isLoading = ApiLoadingState.loading
    })
    builder.addCase(addProjectMilestone.rejected, (state) => {
      state.isLoading = ApiLoadingState.failed
    })
    builder.addCase(getProjectMileStone.pending, (state) => {
      state.isLoading = ApiLoadingState.loading
    })
    builder.addCase(getProjectMileStone.rejected, (state) => {
      state.isLoading = ApiLoadingState.failed
    })
    builder.addCase(getProjectMileStone.fulfilled, (state, action) => {
      state.isLoading = ApiLoadingState.succeeded
      state.mileStonesList = action.payload
    })
    builder.addCase(mileStoneTimeLine.fulfilled, (state, action) => {
      state.isLoading = ApiLoadingState.succeeded
      state.mileStoneHistory = action.payload
    })
    builder.addCase(getMilestone.fulfilled, (state, action) => {
      state.isLoading = ApiLoadingState.succeeded
      state.getMilestone = action.payload
    })
    builder.addCase(getMilestoneNewsFeed.fulfilled, (state, action) => {
      state.isLoading = ApiLoadingState.succeeded
      state.milestoneNewsFeed = action.payload
    })
    builder.addCase(getRaisedInvoices.fulfilled, (state, action) => {
      state.isLoading = ApiLoadingState.succeeded
      state.raisedInvoices = action.payload as RaisedInvoiceType[]
    })
    builder
      .addCase(editProjectMilestone.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(editProjectMilestone.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getMilestone = action.payload as GetMilestone
      })
      .addCase(getPeopleForMilestone.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getPeopleForMilestone = action.payload as GetPeopleForMilestone[]
      })
      .addCase(getMilestoneNumber.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.milestoneNumber = action.payload as number
      })
      .addCase(getCRListForMilestone.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getCRListForMilestone = action.payload as GetCRListForMilestone[]
      })
      .addCase(getWorkDetails.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getWorkDetails = action.payload as GetWorkDetails
      })
      .addCase(milestonePercentage.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.milestonePercentageNumber = action.payload as number
      })
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.projectMileStone?.isLoading

const projectMileStone = (state: RootState): MileStoneResponse[] =>
  state.projectMileStone?.mileStonesList?.list

const projectMileStoneSize = (state: RootState): number =>
  state.projectMileStone?.mileStonesList?.size

const projectMileStoneTimeLine = (state: RootState): MileStoneHistory[] =>
  state.projectMileStone?.mileStoneHistory

const getProjectMileStoneResponse = (state: RootState): GetMilestone =>
  state.projectMileStone?.getMilestone

const projectMileStoneNewsFeed = (state: RootState): MilestoneNewsFeed[] =>
  state.projectMileStone?.milestoneNewsFeed

const getPeopleMilestone = (state: RootState): GetPeopleForMilestone[] =>
  state.projectMileStone.getPeopleForMilestone

const milestoneNumber = (state: RootState): number =>
  state.projectMileStone.milestoneNumber

const getCRListMilestone = (state: RootState): GetCRListForMilestone[] =>
  state.projectMileStone.getCRListForMilestone

const milestoneWorkDetails = (state: RootState): GetWorkDetails =>
  state.projectMileStone.getWorkDetails

const editMilestoneDetails = (state: RootState): GetMilestone =>
  state.projectMileStone.getMilestone

const getMilestonePercentage = (state: RootState): number | string =>
  state.projectMileStone.milestonePercentageNumber

const getIsPagAdd = (state: RootState): boolean =>
  state.projectMileStone.isPagAdd

const getAllRaisedInvoices = (state: RootState): RaisedInvoiceType[] =>
  state.projectMileStone.raisedInvoices

const getObjectFromAdd = (
  state: RootState,
): {
  page: number
  size: number
  endIndex: number
  firstIndex: number
  projectId: string
} => state.projectMileStone.objectForAdd

const mileStoneThunk = {
  getProjectMileStone,
  mileStoneTimeLine,
  getMilestone,
  uploadProjectMileStoneImage,
  postProjectMileStone,
  getMilestoneNewsFeed,
  milestoneDelete,
  closeMilestone,
  reOpenMilestone,
  editProjectMilestone,
  getPeopleForMilestone,
  getMilestoneNumber,
  getCRListForMilestone,
  getWorkDetails,
  addProjectMilestone,
  updateProjectMilestone,
  milestonePercentage,
  getRaisedInvoices,
}

const mileStoneSelectors = {
  isLoading,
  projectMileStone,
  projectMileStoneSize,
  projectMileStoneTimeLine,
  getProjectMileStoneResponse,
  projectMileStoneNewsFeed,
  getPeopleMilestone,
  milestoneNumber,
  getCRListMilestone,
  milestoneWorkDetails,
  editMilestoneDetails,
  getMilestonePercentage,
  getObjectFromAdd,
  getIsPagAdd,
  getAllRaisedInvoices,
}

export const mileStoneService = {
  ...mileStoneThunk,
  actions: mileStoneSlice.actions,
  selectors: mileStoneSelectors,
}

export default mileStoneSlice.reducer
